import React, { useState, useEffect, useRef } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvTooltip, { TriggerType } from 'shared/components/nv-tooltip';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { useFormContext } from 'react-hook-form';
import NvFroala from 'froala/components/nv-froala';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';
import NvIcon from 'shared/components/nv-icon';
import { getCourseAliases } from 'redux/selectors/course';
import { CommonHeaderSection, helpResourceForm, tooltipStyles } from './styles';
import HelpResourcesTemplate, { getHelpResourcesTemplateHtml } from './help-resources-template';
import { config } from '../../../../config/pendo.config.json';

const HelpResourcesSection: React.FC = () => {
  const { setValue, watch } = useFormContext();
  const isHelpResourceEnabled = watch('hasHelpResourcesEnabled');
  const aliases = useSelector(getCourseAliases);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const exampleLinkRef = useRef(null);

  // Function to paste the template into the NvFroala editor
  const handlePasteHelpTemplate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setValue('courseHelpBody', getHelpResourcesTemplateHtml(aliases?.teachingTeamAliases), { shouldDirty: true, shouldValidate: true });
    setValue('courseHelpTitle', t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TEMPLATE_TITLE(), { shouldDirty: true, shouldValidate: true });
    setValue('hasHelpResourcesEnabled', true, { shouldDirty: true, shouldValidate: true });
    // Close the tooltip after pasting the template
    setShowTooltip(false);
  };

  // Function to toggle tooltip visibility
  const handleTooltipToggle = (isVisible: boolean) => {
    setShowTooltip(isVisible);
  };

  // Function to handle click on the example text
  const handleExampleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setShowTooltip(!showTooltip);
  };

  // Effect to add document click listener to close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close the tooltip if clicking on the example link itself
      if (exampleLinkRef.current && exampleLinkRef.current.contains(event.target)) {
        return;
      }

      // Close the tooltip if clicking outside the tooltip area
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    // Add event listener when tooltip is open
    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div>
      {/* HEADER */}
      <CommonHeaderSection>
        <div className='header-container'>
          <NvIcon
            icon='link'
            size='small'
            className='color-primary'
          />
          <div className='title'>
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TITLE()}
          </div>
        </div>
        <div className='description'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.DESCRIPTION()}
        </div>
        <div className='switch-container'>
          <NvSwitch
            withForm
            checked={isHelpResourceEnabled}
            dataQa={isHelpResourceEnabled ? config.pendo.aiLearning.enableHelpResources : config.pendo.aiLearning.disableHelpResources}
            name='hasHelpResourcesEnabled'
            onChange={() => {
              setValue('hasHelpResourcesEnabled', !isHelpResourceEnabled, { shouldDirty: true });
              if (isHelpResourceEnabled) {
                setValue('courseHelp.body', '');
                setValue('courseHelp.title', '');
              }
            }}
            ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.SWITCH_LABEL()}
            enabledLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.ENABLE_LABEL()}
            disabledLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.DISABLED_LABEL()}
          />
          <span className='switch-label'>
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.SWITCH_LABEL()}{' '}
            <span ref={tooltipRef} data-qa={config.pendo.aiLearning.seeExampleHelpResources}>
              <NvTooltip
                className='custom-tooltip'
                css={tooltipStyles}
                show={showTooltip}
                placement={isHelpResourceEnabled ? 'bottom' : 'top'}
                onToggle={handleTooltipToggle}
                triggerType={TriggerType.CLICK}
                text={(
                  <div className='help-resource-template'>
                    <button
                      type='button'
                      className='copy-example'
                      ref={exampleLinkRef}
                      onClick={handlePasteHelpTemplate}
                    >
                      <NvIcon
                        icon='files'
                        size='small'
                        className='color-primary'
                      />
                      {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.COPY_EXAMPLE()}
                    </button>
                    <HelpResourcesTemplate />
                  </div>
                )}
              >
                <span
                  className='switch-highlight'
                  onClick={handleExampleClick}
                  role='button'
                  tabIndex={0}
                  ref={exampleLinkRef}
                  onKeyPress={(e) => e.key === 'Enter' && handleExampleClick(e as any)}
                >
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.SEE_AN_EXAMPLE()}
                </span>
              </NvTooltip>
            </span>
          </span>
        </div>
      </CommonHeaderSection>

      {/* Rest of the component */}
      {isHelpResourceEnabled && (
        <div css={helpResourceForm}>
          <div>
            <label>{t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TITLE_FORM()}</label>
            <NvTextInput
              withForm
              autoComplete='off'
              inputClassName='text-medium'
              placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TYPE_HERE()}
              name='courseHelpTitle'
              ariaLabel={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TITLE()}
              label={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.TITLE()}
              onChange={(e) => setValue('courseHelpTitle', e.target.value, { shouldDirty: true })}
            />
          </div>
          <div>
            <label>{t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.BODY_FORM()}</label>
            <NvFroala
              preset={FroalaViewMode.NORMAL}
              withCodeView
              name='courseHelpBody'
              placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.HELP_RESOURCES.START_TYPING()}
              uploadType={UploadType.NONE}
              withForm
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpResourcesSection;
